import { UIMessage } from "ai";
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import type {
    AlfredContextUsageOptionsInterface,
    AlfredContextUsageStateInterface,
} from "@/interfaces/alfred";

/**
 * Token estimation constants
 * Based on typical token-to-character ratios for English text
 */
// Average characters per token
const CHARS_PER_TOKEN = 4;
// Default context window (128k)
const DEFAULT_MAX_TOKENS = 128000;

/**
 * Formats token count for display (e.g., 12400 -> "12.4k")
 */
const formatTokenCount = (tokens: number): string => {
    if (tokens >= 1000) {
        const k = tokens / 1000;
        // Show one decimal place for values < 100k
        if (k < 100) return `${k.toFixed(1)}k`;

        return `${Math.round(k)}k`;
    }
    return tokens.toString();
};

/**
 * Estimates token count from text content
 * Uses a simple character-based estimation
 */
const estimateTokens = (text: string): number => {
    if (!text) return 0;
    return Math.ceil(text.length / CHARS_PER_TOKEN);
};

/**
 * Extracts text content from UIMessage parts
 */
const getMessageContent = (message: UIMessage): string => {
    if (!message.parts || message.parts.length === 0) return "";

    return message.parts.
        filter((part): part is { text: string, type: "text"; } => part.type === "text").
        map((part) => part.text).
        join("");
};

/**
 * Calculates total tokens from messages array
 */
const calculateTotalTokens = (messages: UIMessage[]): number => messages.reduce(
    (total, message) => {
        const content = getMessageContent(message);
        /* Add overhead for message structure (role, separators, etc.) - Approximate tokens for message metadata */
        const overhead = 4;
        return total + estimateTokens(content) + overhead;
    },
    0,
);

/**
 * Determines severity level based on percentage
 */
const getSeverity = (percentage: number): "low" | "medium" | "high" => {
    if (percentage >= 80) return "high";
    if (percentage >= 50) return "medium";
    return "low";
};

/**
 * useAlfredContextUsage - Hook for tracking and displaying context window usage
 *
 * Features:
 * - Estimates token usage from message content
 * - Supports API-provided token counts when available
 * - Calculates usage percentage and severity level
 * - Provides formatted display text
 * - Tracks warning banner dismissed state
 *
 * Usage:
 * ```tsx
 * const { usedTokens, percentage, severity, displayText, isNearLimit } = useAlfredContextUsage({
 *   messages,
 *   maxTokens: 128000,
 * });
 * ```
 */
export const useAlfredContextUsage = ({
    apiTokenCount,
    maxOutputTokens = DEFAULT_MAX_TOKENS,
    messages,
}: AlfredContextUsageOptionsInterface): AlfredContextUsageStateInterface => {
    const [warningDismissed, setWarningDismissed] = useState(false);

    // Calculate estimated tokens from messages
    const estimatedTokens = useMemo(
        () => calculateTotalTokens(messages),
        [messages],
    );

    // Use API token count if available, otherwise use estimation
    const usedTokens = apiTokenCount ?? estimatedTokens;

    // Calculate percentage
    const percentage = useMemo(
        () => Math.min(
            Math.round((usedTokens / maxOutputTokens) * 100),
            100,
        ),
        [usedTokens, maxOutputTokens],
    );

    // Determine severity
    const severity = useMemo(
        () => getSeverity(percentage),
        [percentage],
    );

    // Check if near limit
    const isNearLimit = percentage >= 80;

    // Format display text
    const displayText = useMemo(
        () => `${formatTokenCount(usedTokens)} / ${formatTokenCount(maxOutputTokens)}`,
        [usedTokens, maxOutputTokens],
    );

    // Dismiss warning handler
    const dismissWarning = useCallback(
        () => setWarningDismissed(true),
        [],
    );

    // Reset warning handler
    const resetWarning = useCallback(
        () => setWarningDismissed(false),
        [],
    );

    // Reset warning dismissed state when messages are cleared (new conversation)
    useEffect(
        () => {
            if (messages.length === 0) setWarningDismissed(false);
        },
        [messages.length],
    );

    return {
        dismissWarning,
        displayText,
        isNearLimit,
        maxOutputTokens,
        percentage,
        resetWarning,
        severity,
        usedTokens,
        warningDismissed,
    };
};

// Legacy exports for backwards compatibility
export type ContextUsageState = AlfredContextUsageStateInterface;
export type UseContextUsageOptions = AlfredContextUsageOptionsInterface;
export const useContextUsage = useAlfredContextUsage;
